import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lock, Unlock, Phone, Mail, MessageCircle, User, Coins, MapPin, BadgeCheck, Loader2, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Panel } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtMoney, fmtDate } from "@/lib/api";

const ContactRow = ({ icon: Icon, label, value, href, testId }) => (
  <div className="flex items-center gap-3 py-3 border-b last:border-0">
    <div className="h-9 w-9 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center"><Icon className="h-4 w-4" /></div>
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      {value ? (href ? <a href={href} className="font-medium hover:text-primary break-all" data-testid={testId}>{value}</a> : <p className="font-medium" data-testid={testId}>{value}</p>) : <p className="text-muted-foreground">—</p>}
    </div>
  </div>
);

const LockedPanel = ({ cost, onUnlock, busy }) => (
  <div data-testid="contact-locked-panel">
    <div className="space-y-3" aria-hidden="true">
      {["Client name", "Phone", "Email", "WhatsApp"].map((k) => (
        <div key={k} className="flex items-center gap-3"><div className="h-9 w-9 rounded-lg bg-muted" /><div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{k}</p><p className="locked-blur font-mono text-sm">•••• ••••••</p></div></div>
      ))}
    </div>
    <div className="mt-6 rounded-lg bg-amber-500/5 border border-amber-500/20 p-4">
      <p className="flex items-center gap-2 text-sm font-semibold"><Lock className="h-4 w-4 text-amber-600" />Contact details are private</p>
      <p className="text-xs text-muted-foreground mt-1">Unlock permanently with credits, or subscribe for access to all leads.</p>
      <Button className="w-full mt-4" onClick={onUnlock} disabled={busy} data-testid="unlock-lead-button">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Coins className="h-4 w-4 mr-2" />}Unlock for {cost} credits
      </Button>
      <Button variant="outline" asChild className="w-full mt-2" data-testid="view-plans-button"><Link to="/app/billing">View subscription plans</Link></Button>
    </div>
  </div>
);

export default function LeadDetail() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const [lead, setLead] = useState(null);
  const [contact, setContact] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/leads/${id}`);
      setLead(data);
      if (data.access) setContact((await api.get(`/leads/${id}/contact`)).data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const unlock = async () => {
    setBusy(true);
    try {
      await api.post(`/leads/${id}/unlock`);
      toast.success("Lead unlocked. Contact details are now available.");
      await load();
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  if (notFound) return <div className="text-center py-20" data-testid="lead-not-found"><p className="font-display text-xl font-semibold">Lead not found</p><Link to="/app/marketplace" className="text-primary text-sm">Back to marketplace</Link></div>;
  if (!lead) return <div className="py-20 text-center text-muted-foreground">Loading...</div>;
  const c = contact?.contact;

  return (
    <div>
      <Link to="/app/marketplace" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-marketplace"><ArrowLeft className="h-4 w-4" />Marketplace</Link>
      <div className="grid lg:grid-cols-[1fr_380px] gap-6 items-start">
        <div className="bg-card rounded-xl border p-6 sm:p-8 fade-up">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-primary">{lead.category}</span>
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400"><BadgeCheck className="h-3.5 w-3.5" />Verified</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mt-3" data-testid="lead-title">{lead.title}</h1>
          <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{[lead.city, lead.country].filter(Boolean).join(", ")}</span>
            {lead.industry && <span>{lead.industry}</span>}
            <span className="inline-flex items-center gap-1.5"><CalendarClock className="h-4 w-4" />{fmtDate(lead.published_at)}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <div className="rounded-lg bg-secondary p-4"><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Budget</p><p className="font-display text-2xl font-bold mt-1" data-testid="lead-budget">{fmtMoney(lead.budget, lead.currency)}</p></div>
            <div className="rounded-lg bg-secondary p-4"><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Unlock cost</p><p className="font-display text-2xl font-bold mt-1">{lead.credit_cost} credits</p></div>
          </div>
          <h3 className="font-semibold mt-8 mb-2">Project description</h3>
          <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-line" data-testid="lead-description">{lead.description}</p>
          {lead.requirements && <><h3 className="font-semibold mt-6 mb-2">Requirements</h3><p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-line">{lead.requirements}</p></>}
        </div>
        <Panel title={<span className="flex items-center gap-2">{c ? <Unlock className="h-4 w-4 text-emerald-500" /> : <Lock className="h-4 w-4" />}Client contact</span>} className="lg:sticky lg:top-24">
          {c ? (
            <div data-testid="contact-unlocked-panel">
              <p className="text-xs text-muted-foreground mb-2" data-testid="contact-access-via">{contact.via === "credits" ? "Permanently unlocked with credits" : "Access via active subscription"}</p>
              <ContactRow icon={User} label="Client name" value={c.client_name} testId="contact-client-name" />
              <ContactRow icon={Phone} label="Phone" value={c.phone} href={`tel:${c.phone}`} testId="contact-phone" />
              <ContactRow icon={Mail} label="Email" value={c.email} href={`mailto:${c.email}`} testId="contact-email" />
              <ContactRow icon={MessageCircle} label="WhatsApp" value={c.whatsapp} href={c.whatsapp ? `https://wa.me/${c.whatsapp.replace(/\D/g, "")}` : undefined} testId="contact-whatsapp" />
              <Button variant="outline" asChild className="w-full mt-4" data-testid="go-to-my-leads"><Link to="/app/my-leads">Manage in My Leads</Link></Button>
            </div>
          ) : (
            <LockedPanel cost={lead.credit_cost} busy={busy} onUnlock={() => setConfirm(true)} />
          )}
        </Panel>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlock this lead?</AlertDialogTitle>
            <AlertDialogDescription>{lead.credit_cost} credits will be deducted. Your balance: {user.credit_balance ?? 0} credits. Access is permanent for your agency.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unlock-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={unlock} data-testid="unlock-confirm-button">Unlock now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
